import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, Eye, FileSpreadsheet, Pencil, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { applyLabelStyle, applyWorksheetStyle, styleWorkbook } from "../excel";
import { formatDate } from "./catalogs";
import { Button, CatalogLabel, ConfirmDialog, EmptyState, FormField, Modal, NumericInput, Panel } from "../components/ui";
import { formatNumber } from "../numbers";
import type { CalculationResult, CalculationVoucher, CalculationVoucherDetail } from "../types";

export function CalculationVoucherWorkspace({
  calculationId,
  onUpdated,
}: {
  calculationId: string | null;
  onUpdated?(): void;
}) {
  const detail = useQuery({
    queryKey: ["calculation-voucher", calculationId],
    queryFn: () => api<CalculationVoucherDetail>(`/calculations/${calculationId}`),
    enabled: Boolean(calculationId),
  });
  if (!calculationId) return <EmptyState title="Chưa có phiếu tính" detail="Nhập kế hoạch sản xuất và chọn “Tính nhu cầu” để lập phiếu tính toán." />;
  if (detail.isLoading) return <div className="loading-space">Đang lập phiếu tính toán…</div>;
  if (!detail.data) return <EmptyState title="Không thể mở phiếu tính" detail="Vui lòng thử tính lại hoặc kiểm tra lịch sử phiếu tính." />;
  return <VoucherContent voucher={detail.data} onUpdated={onUpdated} />;
}

export function CalculationDetailModal({
  calculationId,
  open,
  mode = "view",
  onClose,
  onUpdated,
}: {
  calculationId: string | null;
  open: boolean;
  mode?: "view" | "edit";
  onClose(): void;
  onUpdated?(): void;
}) {
  const detail = useQuery({
    queryKey: ["calculation-voucher", calculationId],
    queryFn: () => api<CalculationVoucherDetail>(`/calculations/${calculationId}`),
    enabled: open && Boolean(calculationId),
  });
  return (
    <Modal open={open} title={mode === "edit" ? "Điều chỉnh phiếu tính" : "Chi tiết phiếu tính"} onClose={onClose}>
      <div className="voucher-modal">
        {detail.isLoading ? <div className="loading-space">Đang tải phiếu tính…</div> : detail.data ? <VoucherContent voucher={detail.data} compact mode={mode} onUpdated={onUpdated} /> : <EmptyState title="Không tìm thấy phiếu" detail="Phiếu tính có thể đã không còn tồn tại." />}
      </div>
    </Modal>
  );
}

function VoucherContent({
  voucher,
  compact = false,
  mode = "view",
  onUpdated,
}: {
  voucher: CalculationVoucherDetail;
  compact?: boolean;
  mode?: "view" | "edit";
  onUpdated?(): void;
}) {
  const [edit, setEdit] = useState<CalculationResult | null>(null);
  return (
    <div className={compact ? "voucher-content compact" : "voucher-content"}>
      <div className="voucher-heading">
        <div>
          <span>Phiếu tính toán</span>
          <strong>{voucher.code}</strong>
        </div>
        <div className="voucher-actions">
          {!voucher.confirmedAt && <ConfirmVoucherUse voucher={voucher} onUpdated={onUpdated} />}
          <Button variant="outline" onClick={() => void exportVoucherPdf(voucher)}><Download size={16} /> Tải PDF</Button>
          <Button variant="outline" onClick={() => void exportVoucherXlsx(voucher)}><FileSpreadsheet size={16} /> Xuất Excel</Button>
        </div>
      </div>
      {mode === "edit" && !voucher.confirmedAt && <p className="voucher-edit-hint">Điều chỉnh định mức bằng biểu tượng bút tại từng dòng vật tư. Sau khi chốt, phiếu sẽ không thể sửa hoặc xóa.</p>}
      <div className="voucher-info-grid">
        <dl>
          <div><dt>Hợp đồng</dt><dd>{voucher.contractNo || "—"}</dd></div>
          <div><dt>Khách hàng</dt><dd>{voucher.customer ? <CatalogLabel code={voucher.customer.code} name={voucher.customer.name} /> : "—"}</dd></div>
          <div><dt>Sản phẩm</dt><dd>{voucher.product ? <CatalogLabel code={voucher.product.code} name={voucher.product.name} /> : "—"}</dd></div>
          <div><dt>Số lượng sản xuất</dt><dd>{formatNumber(voucher.productionQuantity)}</dd></div>
          <div><dt>Thời gian lập</dt><dd>{formatDate(voucher.createdAt)}</dd></div>
          <div><dt>Trạng thái</dt><dd>{voucher.confirmedAt ? `Đã chốt lúc ${formatDate(voucher.confirmedAt)}` : "Đang lập"}</dd></div>
        </dl>
        <div className="voucher-summary">
          <span>Tổng nhu cầu</span><strong>{formatNumber(voucher.totalRequired)}</strong>
          <span>Cần mua / cấp</span><strong>{formatNumber(voucher.totalToSupply)}</strong>
        </div>
      </div>
      <Panel title="Chi tiết nguyên vật liệu">
        <div className="table-scroll">
          <table className="voucher-table">
            <thead><tr><th>#</th><th>Vật tư</th><th>ĐVT</th><th>Định mức gốc</th><th>Định mức điều chỉnh</th><th>Tổng nhu cầu</th><th>Tồn kho</th><th>Cần mua / cấp</th><th>Ghi chú</th><th /></tr></thead>
            <tbody>
              {voucher.results.map((result, index) => {
                const latest = voucher.changes.find((change) => change.materialId === result.materialId && change.unitId === result.unitId);
                return <tr key={`${result.materialId}-${result.unitId}`}>
                  <td>{index + 1}</td><td><CatalogLabel code={result.materialCode} name={result.materialName} />{Number(result.netRequired) > 0 && <small className={result.supplier ? "material-supplier" : "material-supplier missing"}>{result.supplier ? `Nhà cung cấp: ${result.supplier}` : "Chưa có nhà cung cấp"}</small>}</td><td>{result.unitName}</td>
                  <td className="num-cell">{formatNumber(result.baseNorm)}</td>
                  <td className="num-cell"><span className="editable-number">{formatNumber(result.adjustedNorm)}<button type="button" disabled={Boolean(voucher.confirmedAt)} title="Điều chỉnh định mức" onClick={() => setEdit(result)}><Pencil size={14} /></button></span></td>
                  <td className="num-cell strong">{formatNumber(result.grossRequired)}</td><td className="num-cell">{formatNumber(result.inventory)}</td><td className="num-cell strong">{formatNumber(result.netRequired)}</td>
                  <td>{latest?.note || "—"}</td><td><button className="icon-action" type="button" title="Xem thay đổi của vật tư" onClick={() => setEdit(result)}><Eye size={16} /></button></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Các thay đổi định mức và ghi chú">
        {!voucher.changes.length ? <p className="voucher-empty">Chưa có điều chỉnh nào. Định mức gốc đang được áp dụng cho phiếu này.</p> : <ol className="change-timeline">
          {voucher.changes.map((change) => <li key={change.id}><div><strong>{change.materialCode} — {change.materialName}</strong><span>{formatDate(change.createdAt)} · {change.createdBy}</span></div><p>Điều chỉnh từ <b>{formatNumber(change.previousNorm)}</b> thành <b>{formatNumber(change.adjustedNorm)}</b>. {change.note || "Không có ghi chú."}</p></li>)}
        </ol>}
      </Panel>
      <EditNormModal voucher={voucher} result={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onUpdated?.(); }} />
    </div>
  );
}

function ConfirmVoucherUse({ voucher, onUpdated }: { voucher: CalculationVoucherDetail; onUpdated?(): void }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  async function confirm() {
    setSaving(true);
    setError("");
    try {
      await api(`/calculations/${voucher.id}/confirm-use`, { method: "POST" });
      await Promise.all([
        client.invalidateQueries({ queryKey: ["calculation-voucher", voucher.id] }),
        client.invalidateQueries({ queryKey: ["calculation-vouchers"] }),
        client.invalidateQueries({ queryKey: ["inventory"] }),
      ]);
      setOpen(false);
      onUpdated?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể chốt phiếu tính.");
    } finally {
      setSaving(false);
    }
  }
  return <><Button variant="success" onClick={() => { setError(""); setOpen(true); }}><CheckCircle2 size={16} /> Chốt và trừ tồn</Button><ConfirmDialog open={open} title={error ? "Không thể chốt phiếu" : "Chốt phiếu và trừ tồn kho"} message={error || "Hệ thống sẽ ghi giảm tồn kho theo định mức điều chỉnh trên phiếu này. Phiếu đã chốt sẽ không thể sửa hoặc xóa."} confirmLabel="Chốt phiếu" variant="primary" pending={saving} onClose={() => setOpen(false)} onConfirm={() => void confirm()} /></>;
}

function EditNormModal({
  voucher,
  result,
  onClose,
  onSaved,
}: {
  voucher: CalculationVoucherDetail;
  result: CalculationResult | null;
  onClose(): void;
  onSaved(): void;
}) {
  const client = useQueryClient();
  const [norm, setNorm] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (result) { setNorm(result.adjustedNorm); setNote(""); setError(""); } }, [result]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!result) return;
    setSaving(true); setError("");
    try {
      await api(`/calculations/${voucher.id}/materials/${result.materialId}/${result.unitId}`, { method: "PATCH", body: JSON.stringify({ adjustedNorm: norm, note }) });
      await client.invalidateQueries({ queryKey: ["calculation-voucher", voucher.id] });
      await client.invalidateQueries({ queryKey: ["calculation-vouchers"] });
      onSaved();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Không thể điều chỉnh định mức."); }
    finally { setSaving(false); }
  }
  return <Modal open={Boolean(result)} title="Điều chỉnh định mức theo phiếu" onClose={onClose}>
    <form className="modal-form" onSubmit={save}>
      <p className="muted">Điều chỉnh chỉ áp dụng cho phiếu {voucher.code}; BOM gốc không thay đổi.</p>
      <FormField label="Vật tư"><input readOnly value={result ? `${result.materialCode} — ${result.materialName}` : ""} /></FormField>
      <FormField label="Định mức điều chỉnh"><NumericInput required value={norm} onValueChange={setNorm} /></FormField>
      <FormField label="Lý do / ghi chú"><textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} placeholder="Ví dụ: Điều chỉnh theo thay đổi quy cách sản xuất" /></FormField>
      {error && <p className="form-error">{error}</p>}
      <div className="modal-actions"><Button type="button" variant="outline" onClick={onClose} disabled={saving}>Hủy</Button><Button type="submit" disabled={saving}><Save size={16} /> Lưu điều chỉnh</Button></div>
    </form>
  </Modal>;
}

export function createVoucherWorksheet(XLSX: typeof import("xlsx-js-style"), voucher: CalculationVoucher) {
  const header = [
    ["PHIẾU TÍNH TOÁN VẬT TƯ"],
    ["Mã phiếu", voucher.code, "Hợp đồng", voucher.contractNo],
    ["Khách hàng", voucher.customer?.name ?? "", "Sản phẩm", voucher.product?.name ?? ""],
    ["Số lượng sản xuất", Number(voucher.productionQuantity), "Thời gian lập", new Date(voucher.createdAt)],
    [],
    ["STT", "Mã vật tư", "Tên vật tư", "Nhà cung cấp", "ĐVT", "Định mức gốc", "Định mức điều chỉnh", "Tổng nhu cầu", "Tồn kho", "Cần mua / cấp", "Ghi chú"],
    ...voucher.results.map((result, index) => [index + 1, result.materialCode, result.materialName, result.supplier ?? "", result.unitCode, Number(result.baseNorm), Number(result.adjustedNorm), Number(result.grossRequired), Number(result.inventory), Number(result.netRequired), ""]),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(header);
  sheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }];
  applyWorksheetStyle(XLSX, sheet, { titleRows: [0], headerRows: [5], dataStartsAt: 6, numberColumns: [0, 5, 6, 7, 8, 9], columnWidths: [7, 16, 32, 26, 10, 18, 22, 16, 14, 18, 30] });
  applyLabelStyle(XLSX, sheet, [1, 2, 3], [0, 2]);
  const createdAt = sheet["D4"];
  if (createdAt) {
    createdAt.z = "dd/mm/yyyy hh:mm";
    createdAt.s = { ...(createdAt.s ?? {}), alignment: { horizontal: "center", vertical: "center" }, numFmt: "dd/mm/yyyy hh:mm" };
  }
  return sheet;
}

export function voucherSheetName(voucher: CalculationVoucher, usedNames = new Set<string>()) {
  const base = (voucher.title || voucher.code || "Phiếu tính").replace(/[\\/:?*\[\]]/g, "-").slice(0, 31) || "Phiếu tính";
  let name = base;
  let suffix = 2;
  while (usedNames.has(name)) {
    const ending = ` (${suffix++})`;
    name = `${base.slice(0, 31 - ending.length)}${ending}`;
  }
  usedNames.add(name);
  return name;
}

export async function exportVoucherXlsx(voucher: CalculationVoucher) {
  const XLSX = await import("xlsx-js-style");
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, createVoucherWorksheet(XLSX, voucher), voucherSheetName(voucher));
  styleWorkbook(XLSX, workbook, voucher.title || voucher.code);
  XLSX.writeFile(workbook, `${voucher.code}.xlsx`);
}

export async function exportVoucherPdf(voucher: CalculationVoucher) {
  const rowsPerPage = 13;
  const pages = Array.from({ length: Math.max(1, Math.ceil(voucher.results.length / rowsPerPage)) }, (_, page) => drawVoucherPage(voucher, voucher.results.slice(page * rowsPerPage, (page + 1) * rowsPerPage), page + 1, Math.ceil(voucher.results.length / rowsPerPage)));
  const images = pages.map((canvas) => dataUrlToBytes(canvas.toDataURL("image/jpeg", 0.92)));
  const pdf = makeImagePdf(images, pages[0]?.width ?? 1600, pages[0]?.height ?? 1130);
  downloadBlob(new Blob([pdf], { type: "application/pdf" }), `${voucher.code}.pdf`);
}

function drawVoucherPage(voucher: CalculationVoucher, rows: CalculationResult[], page: number, totalPages: number) {
  const canvas = document.createElement("canvas"); canvas.width = 1600; canvas.height = 1130;
  const ctx = canvas.getContext("2d"); if (!ctx) return canvas;
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#075dbc"; ctx.fillRect(0, 0, 1600, 94);
  ctx.fillStyle = "#ffffff"; ctx.font = "700 32px Arial"; ctx.fillText("PHIẾU TÍNH TOÁN VẬT TƯ", 58, 58);
  ctx.font = "18px Arial"; ctx.fillText(`Mã phiếu: ${voucher.code} · Trang ${page}/${totalPages}`, 58, 82);
  ctx.fillStyle = "#1d2b3e"; ctx.font = "600 20px Arial";
  const details = [`Hợp đồng: ${voucher.contractNo || "—"}`, `Khách hàng: ${voucher.customer?.name ?? "—"}`, `Sản phẩm: ${voucher.product?.name ?? "—"}`, `Số lượng SX: ${formatNumber(voucher.productionQuantity)}`, `Lập lúc: ${formatDate(voucher.createdAt)}`];
  details.forEach((text, index) => ctx.fillText(text, 58 + (index % 3) * 510, 142 + Math.floor(index / 3) * 34));
  const top = 220; const columns = [58, 118, 290, 620, 700, 850, 1010, 1160, 1305, 1460, 1550];
  const headers = ["#", "Mã vật tư", "Tên vật tư", "ĐVT", "ĐM gốc", "ĐM điều chỉnh", "Tổng nhu cầu", "Tồn kho", "Cần mua/cấp", "Ghi chú"];
  ctx.fillStyle = "#e9f3ff"; ctx.fillRect(58, top, 1492, 52); ctx.strokeStyle = "#bcd1e7"; ctx.lineWidth = 1;
  ctx.fillStyle = "#1f3c5d"; ctx.font = "700 15px Arial"; headers.forEach((text, index) => ctx.fillText(text, (columns[index] ?? 0) + 8, top + 31));
  rows.forEach((row, index) => {
    const y = top + 52 + index * 55;
    ctx.fillStyle = index % 2 ? "#ffffff" : "#f8fbff"; ctx.fillRect(58, y, 1492, 55);
    ctx.fillStyle = "#26384f"; ctx.font = "16px Arial";
    const cells = [String(index + 1), row.materialCode, row.materialName, row.unitCode, formatNumber(row.baseNorm), formatNumber(row.adjustedNorm), formatNumber(row.grossRequired), formatNumber(row.inventory), formatNumber(row.netRequired), ""];
    cells.forEach((text, cell) => ctx.fillText(text.length > 21 ? `${text.slice(0, 20)}…` : text, (columns[cell] ?? 0) + 8, y + 33));
  });
  const end = top + 52 + Math.max(rows.length, 1) * 55;
  ctx.strokeStyle = "#bcd1e7"; for (let index = 0; index < columns.length; index += 1) { const column = columns[index] ?? 0; ctx.beginPath(); ctx.moveTo(column, top); ctx.lineTo(column, end); ctx.stroke(); }
  ctx.beginPath(); ctx.moveTo(1550, top); ctx.lineTo(1550, end); ctx.stroke();
  ctx.fillStyle = "#075dbc"; ctx.font = "700 20px Arial"; ctx.fillText(`Tổng nhu cầu: ${formatNumber(voucher.totalRequired)}`, 1050, end + 58); ctx.fillText(`Cần mua / cấp: ${formatNumber(voucher.totalToSupply)}`, 1050, end + 90);
  ctx.fillStyle = "#60728a"; ctx.font = "15px Arial"; ctx.fillText("BOM System · Phòng Kế hoạch", 58, 1080);
  return canvas;
}

function dataUrlToBytes(dataUrl: string) { const binary = atob(dataUrl.split(",")[1] ?? ""); return Uint8Array.from(binary, (char) => char.charCodeAt(0)); }
function ascii(value: string) { return new TextEncoder().encode(value); }
function mergeBytes(parts: Uint8Array[]) { const length = parts.reduce((total, part) => total + part.length, 0); const output = new Uint8Array(length); let offset = 0; for (const part of parts) { output.set(part, offset); offset += part.length; } return output; }
function makeImagePdf(images: Uint8Array[], width: number, height: number) {
  const bodies: Uint8Array[] = []; const pageIds = images.map((_, index) => 3 + index * 3);
  bodies.push(ascii("<< /Type /Catalog /Pages 2 0 R >>"));
  bodies.push(ascii(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${images.length} >>`));
  images.forEach((image, index) => {
    const pageId = 3 + index * 3; const imageId = pageId + 1; const contentId = pageId + 2;
    bodies.push(ascii(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 842 595] /Resources << /XObject << /Im${index} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`));
    bodies.push(mergeBytes([ascii(`<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.length} >>\nstream\n`), image, ascii("\nendstream")]));
    const commands = ascii(`q\n842 0 0 595 0 0 cm\n/Im${index} Do\nQ\n`); bodies.push(mergeBytes([ascii(`<< /Length ${commands.length} >>\nstream\n`), commands, ascii("endstream")]));
  });
  const chunks: Uint8Array[] = [ascii("%PDF-1.4\n%âãÏÓ\n")]; const offsets = [0]; let offset = chunks[0]?.length ?? 0;
  bodies.forEach((body, index) => { offsets.push(offset); const object = mergeBytes([ascii(`${index + 1} 0 obj\n`), body, ascii("\nendobj\n")]); chunks.push(object); offset += object.length; });
  const xref = offset; let index = `xref\n0 ${bodies.length + 1}\n0000000000 65535 f \n`; offsets.slice(1).forEach((position) => { index += `${String(position).padStart(10, "0")} 00000 n \n`; });
  chunks.push(ascii(`${index}trailer\n<< /Size ${bodies.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`));
  return mergeBytes(chunks);
}
function downloadBlob(blob: Blob, filename: string) { const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = filename; link.click(); setTimeout(() => URL.revokeObjectURL(url), 0); }
